import { useDataQuery } from '@hooks/useData/useDataQuery';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@utils/serverClient';

export interface MusicControl {
  id: string;
  screen_id: string;
  location_id: string;
  is_owner: boolean;
  has_control: boolean;
  requested_at: string | null;
  request_status: 'pending' | 'approved' | 'rejected' | null;
}

export function useMusicControl(screenId: string, locationId?: string) {
  const queryClient = useQueryClient();

  const { data: controlInfos, isLoading } = useDataQuery({
    options: {
      enabled: !!locationId,
      refetchOnMount: false,
    },
    queryKey: ['music-control', locationId],
    async queryFn() {
      const { data, error } = await supabase
        .from('music_control')
        .select('*')
        .eq('location_id', locationId!)

      if (error) throw error;
      return data as MusicControl[];
    }
  });

  const requestControl = useMutation(
    {
      async mutationFn() {
        const { data, error } = await supabase
          .from('music_control')
          .upsert({
            requested_at: new Date().toISOString(),
            request_status: 'pending',
            is_owner: false,
            has_control: false,
            screen_id: screenId,
            location_id: locationId!,
          }, { onConflict: "screen_id,location_id" })
          .select()
          .single();

        if (error) throw error;
        return data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['music-control', locationId],
          exact: true,
        })
      },
    }
  );

  const approveControl = useMutation(
    {
      async mutationFn(targetScreenId: string) {
        const { data, error } = await supabase
          .from('music_control')
          .update({
            request_status: 'approved',
            has_control: true,
          })
          .eq('screen_id', targetScreenId)
          .eq('location_id', locationId!)
          .select()
          .single();

        if (error) throw error;
        return data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['music-control'],
          exact: false,
        });
      },
    }
  );

  const rejectControl = useMutation(
    {
      async mutationFn(targetScreenId: string) {
        const { data, error } = await supabase
          .from('music_control')
          .update({
            request_status: 'rejected',
            has_control: false,
          })
          .eq('screen_id', targetScreenId)
          .eq('location_id', locationId!)
          .select()
          .single();

        if (error) throw error;
        return data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['music-control'],
          exact: false,
        });
      },
    }
  );

  const revokeControl = useMutation(
    {
      async mutationFn(targetScreenId: string) {
        const { data, error } = await supabase
          .from('music_control')
          .update({
            request_status: null,
            has_control: false,
            requested_at: null,
          })
          .eq('screen_id', targetScreenId)
          .eq('location_id', locationId!)
          .select()
          .single();

        if (error) throw error;
        return data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['music-control'],
          exact: false,
        });
      },
    }
  );

  const controlInfo = controlInfos?.find(control => control.screen_id === screenId);

  return {
    controlInfos,
    controlInfo,
    isLoading,
    hasControl: controlInfo?.has_control ?? false,
    requestControl,
    approveControl,
    rejectControl,
    revokeControl,
  };
}